import React, { useEffect, useState } from "react"

import { Loader2 } from "lucide-react"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { useAuth } from "@/contexts/AuthContext"
import { useCreateTrackPodAccount } from "@/hooks/useCreateTrackPodAccount"
import { Person } from "@/types/person"

interface AccountsTabProps {
  person: Person
}

const AccountsTab: React.FC<AccountsTabProps> = ({ person }) => {
  const [personData, setPersonData] = useState(person)
  const { claims } = useAuth()
  const canUpdateAccounts = claims.team_permissions?.includes("update.tab.accounts")
  const isCollectionsDriver = personData.sheriff_areas?.includes("Collections") ?? false

  const createTrackPodAccount = useCreateTrackPodAccount()

  useEffect(() => {
    setPersonData(person)
  }, [person])

  const handleCreateTrackPodAccount = () => {
    createTrackPodAccount.mutate(
      { peopleId: personData.id },
      {
        onSuccess: (data) => {
          if (data.username) {
            setPersonData((current) => ({ ...current, track_pod_username: data.username }))
          }

          toast.success(
            data.status === "skipped_already_exists"
              ? "This person already had a Track-POD account"
              : `Track-POD account created${data.username ? `: ${data.username}` : ""}`,
          )
        },
        onError: (error) => {
          toast.error(error.message || "Failed to create Track-POD account")
        },
      },
    )
  }

  return (
    <div className="space-y-6">
      {/* System Accounts - Read Only */}
      <Card>
        <CardHeader>
          <CardTitle>System Accounts (Read Only)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-palette-jungleGreen700">
                Track-POD Username
              </label>
              <p className="mt-1 text-sm text-palette-jungleGreen900">
                {personData.track_pod_username || "Not assigned"}
              </p>
              {!personData.track_pod_username && canUpdateAccounts && isCollectionsDriver && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={handleCreateTrackPodAccount}
                    disabled={createTrackPodAccount.isPending || !personData.person_id}
                  >
                    {createTrackPodAccount.isPending && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    Create Track-POD account
                  </Button>
                  {!personData.person_id && (
                    <p className="mt-1 text-xs text-palette-jungleGreen500">
                      Needs a Person ID first — the Track-POD username is built from it.
                    </p>
                  )}
                </>
              )}
            </div>
            <div>
              <label className="text-sm font-medium text-palette-jungleGreen700">Zerity</label>
              <p className="mt-1 text-sm text-palette-jungleGreen900">
                {personData.zerity_id || "Not assigned"}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default AccountsTab
